import { useEffect, useState, type ReactNode } from 'react';
import { LanguageContext, loadEnglishDictionary, getEnglishDictionary, translations, type Language, type TranslationKey } from './LanguageContext';
import { useAuthStore } from '../store/authStore';

function canSwitchLanguageForUser(user: ReturnType<typeof useAuthStore.getState>['user']) {
  if (import.meta.env.DEV) return true;
  if (!user?.is_admin) return false;
  return `${user.name || ''} ${user.email || ''}`.toLowerCase().includes('losrecomendados');
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const user = useAuthStore((state) => state.user);
  const canSwitchLanguage = canSwitchLanguageForUser(user);
  const [language, setLanguageState] = useState<Language>('es');
  const [englishLoaded, setEnglishLoaded] = useState(false);
  const setLanguage = (nextLanguage: Language) => setLanguageState(canSwitchLanguage ? nextLanguage : 'es');
  const toggleLanguage = () => setLanguageState((current) => canSwitchLanguage && current === 'es' ? 'en' : 'es');
  useEffect(() => {
    if (canSwitchLanguage && localStorage.getItem('tc_lang') === 'en') setLanguageState('en');
    if (!canSwitchLanguage) setLanguageState('es');
  }, [canSwitchLanguage]);
  useEffect(() => {
    if (canSwitchLanguage) localStorage.setItem('tc_lang', language);
    else localStorage.setItem('tc_lang', 'es');
    document.documentElement.lang = canSwitchLanguage ? language : 'es';
  }, [canSwitchLanguage, language]);
  useEffect(() => {
    if (language !== 'en' || englishLoaded) return;
    let cancelled = false;
    loadEnglishDictionary()
      .then(() => { if (!cancelled) setEnglishLoaded(true); })
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, [language, englishLoaded]);
  const t = (key: string) => {
    const dictionary = language === 'en' && englishLoaded ? getEnglishDictionary() ?? translations.es : translations.es;
    return dictionary[key as TranslationKey] ?? translations.es[key as TranslationKey] ?? key;
  };
  return <LanguageContext.Provider value={{ language, setLanguage, toggleLanguage, t, canSwitchLanguage }}>{children}</LanguageContext.Provider>;
}